/**
 * SchoolList
 * 
 * @Author: Focci
 * @Date: 2023-07-24 22:03:06
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-24 22:03:06
 */

import PropTypes from 'prop-types'
import clsx from 'clsx'
import Scrollbar from '@comp/Scrollbar'
import NoData from '@comp/NoData'
import useFetch from '@hook/useFetch'
import { toSlug } from '@lib/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { List, ListItem, ListItemButton, Chip, Button, Skeleton } from '@comp/material'
import { isArray, times, map } from 'lodash-es'
import { useTranslations } from 'next-intl'

function CustomChip({
  label = '',
  isPrivate = false
}) {
  return (
    <Chip 
      label={label}
      size="small"
      sx={{ fontSize: '.6rem', p: 0 }}
      className={clsx(['rounded-sm', isPrivate && 'text-[#34A4F9]'])} 
    />
  )
}

/**
 * 学校类型(小学，初中，高中...)
 */
function SchoolTypeTab({
  districtId = null,
  schoolType = null,
  setSchoolType = () => {}
}) {
  const { data, isLoading } = useFetch(`school-types/${districtId}`)
  const handleChange = useCallback((item) => {
    setSchoolType(item.key)
  }, [setSchoolType])

  useEffect(() => {
    setSchoolType((prev) => {
      const typeKeys = map(isArray(data) ? data : [], 'key')
      return typeKeys.indexOf(prev) > -1
        ? prev
        : (typeKeys[0] || prev)
    })
  }, [data, setSchoolType])

  if(isLoading) {
    return(
      <div className="flex gap-2 ml-1">
        <Skeleton variant="rectangular" style={{ width: '3.2rem', height: '1.625rem' }} />
        <Skeleton variant="rectangular" style={{ width: '3.2rem', height: '1.625rem' }} />
      </div>
    )
  }

  return (
    <div className="flex gap-2 ml-1">
      {isArray(data) && data.map((item) => (
        <Button 
          key={item.key} 
          size="small" 
          color={schoolType === item.key ? 'primary' : 'gray'}
          onClick={() => handleChange(item)}
        >
          {item.value}
        </Button>
      ))}
    </div>
  )
}

function SchoolListBodySkeleton() {
  return(
    <div className="border-t border-t-solid mt-2 ml-1 pt-2 flex-1">
      <div className="flex flex-col gap-y-4">
        {times(5, String).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular" 
            className="w-full h-4 rounded-sm"
            style={{ width: `${((12 - index) / 12) * 100}%` }}
          />
        ))}
      </div>
    </div>
  )
}

function SchoolListBody({
  schoolType = '',
  districtId = null,
  onChange = () => {}
}) {
  const ts = useTranslations('search')
  const { data, isLoading } = useFetch(`district-schools/${districtId}/${schoolType}`)

  const schools = useMemo(() => (map(
    isArray(data) ? data : [], 
    (item) => ({ ...item, slug: item.slug || toSlug(item.pure_label) })
  )), [data])

  const handleClick = useCallback((item) => {
    const type = 'school'
    onChange({ 
      type, 
      data: [{ ...item, type, value: item.id }]
    })
  }, [onChange])

  if(isLoading) {
    return <SchoolListBodySkeleton />
  }

  if(schools.length === 0) {
    return (
      <div className="mt-4 bg-cf5 p-8 mx-1">
        <NoData />
      </div>
    )
  }

  return (
    <div className="border-t border-t-solid mt-2 pt-2 flex-1">
      <Scrollbar>
        <List className="pt-0">
          {schools.map((item) => (
            <ListItem disablePadding key={item.id}>
              <ListItemButton 
                className="py-2.5 flex items-center"
                onClick={() => handleClick(item)}
              >
                <div>
                  <div>
                    {item.label}
                    {/* {item.label !== item.sub_label ? ` (${item.sub_label})` : ''} */}
                  </div>
                  <div className="mt-1 text-meta flex items-center gap-x-1 text-f.6">
                    {item.authority && (
                      <CustomChip 
                        label={item.authority}
                        isPrivate={item.authority_source === 'private'}
                      />
                    )}
                    {item.year_range && (
                      <>
                        <span className="h-1 w-1 bg-cd8 rounded-full" />
                        <span>{`${ts('level')}:${item.year_range}`}</span>
                      </>
                    )}
                    {item.gender && (
                      <>
                        <span className="h-1 w-1 bg-cd8 rounded-full" />
                        <span>{item.gender}</span>
                      </>
                    )}
                    {item.score_type && (
                      <>
                        <span className="h-1 w-1 bg-cd8 rounded-full" />
                        <span>{`${item.score_type}:${item.score}`}</span>
                      </>
                    )}
                    {item.decile && item.decile !== '0' && (
                      <>
                        <span className="h-1 w-1 bg-cd8 rounded-full" />
                        <span>{`Decile:${item.decile}`}</span>
                      </>
                    )}
                  </div>
                </div>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Scrollbar>
    </div>
  )
}

SchoolListBody.propTypes = {
  schoolType: PropTypes.string,
  districtId: PropTypes.number,
}

export default function SchoolList({
  districtId = null,
  onChange = () => {}
}) {
  const [schoolType, setSchoolType] = useState('')
  
  return (
    <>
      <SchoolTypeTab 
        districtId={districtId}
        schoolType={schoolType}
        setSchoolType={setSchoolType}
      />
      {!schoolType && <SchoolListBodySkeleton />}
      {schoolType && (
        <SchoolListBody 
          schoolType={schoolType}
          districtId={districtId}
          onChange={onChange}
        />
      )}
    </>
  )
}

SchoolList.propTypes = {
  districtId: PropTypes.number,
}
